<script setup lang="ts">
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { DataTableWrapper, DeleteDialog } from '@haskoning/vue-component-library'

import { useHardwareProfileStore } from '@/stores/hardwareProfile'
import type { HardwareProfile, HardwareProfileFormPayload } from '@/stores/type/calculation.type'
import HardwareProfileDialog from '@/components/hardware-profile/HardwareProfileDialog.vue'
import HardwareProfileTableRow from '@/components/hardware-profile/HardwareProfileTableRow.vue'

const props = defineProps<{
  title: string
  scope: 'global' | 'project'
  projectId?: number | null
  canManage: boolean
}>()

const hardwareProfileStore = useHardwareProfileStore()
const {
  items,
  selectedItems,
  currentItem,
  createUpdateDialog,
  deleteDialog,
  deleteItem,
  fetchLoading,
  writeLoading,
  deleteLoading
} = storeToRefs(hardwareProfileStore)

const actionHeaders = computed(() => [
  { title: 'Edit', align: props.canManage ? 'center' : ' d-none' },
  { title: 'Delete', align: props.canManage ? 'center' : ' d-none' }
])

const vmHeaders = computed(() => [
  { title: 'Name', align: 'left', width: '220px' },
  { title: 'Description', align: 'left' },
  { title: 'Region', align: 'left', width: '150px' },
  { title: 'VM Size', align: 'left', width: '180px' },
  { title: 'VM Image', align: 'left', width: '220px' },
  { title: 'OS Disk', align: 'left', width: '180px' },
  { title: 'Spot', align: 'center', width: '90px' },
  { title: 'CPU', align: 'right', width: '100px' },
  { title: 'RAM', align: 'right', width: '100px' },
  { title: 'Scope', align: 'left', width: '180px' },
  { title: 'Date Modified', align: 'left', width: '180px' },
  ...actionHeaders.value
])

const scopedProjectId = computed(() =>
  props.scope === 'project' ? (props.projectId ?? null) : null
)

const sortHardwareProfilesByName = (profiles: HardwareProfile[]) => {
  return [...profiles].sort((left, right) => {
    const nameComparison = left.name.localeCompare(right.name, undefined, {
      sensitivity: 'base',
      numeric: true
    })
    if (nameComparison !== 0) return nameComparison
    return left.id - right.id
  })
}

const vmItems = computed(() =>
  sortHardwareProfilesByName(
    (Array.isArray(items.value) ? items.value : []).filter(
      (item) => item.hardware_type === 'VIRTUAL_MACHINE'
    )
  )
)

const filterItemsBySearch = <T extends HardwareProfile>(
  items: T[],
  search: string,
  keys: (keyof T)[]
): T[] => {
  if (!search) return items
  return items.filter((item) =>
    keys.some((key) => item[key]?.toString().toLowerCase().includes(search.toLowerCase()))
  )
}

const getFilteredItems = (items: HardwareProfile[], search: string): HardwareProfile[] => {
  return filterItemsBySearch(items, search, [
    'name',
    'description',
    'hardware_type',
    'deployment_region',
    'vm_size',
    'vm_image',
    'vm_os_disk_storage_account_type'
  ])
}

const fetchProfiles = async () => {
  if (props.scope === 'global') {
    await hardwareProfileStore.fetchGlobal()
    return
  }
  if (props.projectId) {
    await hardwareProfileStore.fetchProject(props.projectId)
  }
}

const addHardwareProfile = () => {
  const profile = hardwareProfileStore.createNewHardwareProfile(scopedProjectId.value)
  hardwareProfileStore.setCurrentItem(profile)
  hardwareProfileStore.openCreateUpdateDialog()
}

const editHardwareProfile = (item: HardwareProfile) => {
  hardwareProfileStore.setCurrentItem({ ...item })
  hardwareProfileStore.openCreateUpdateDialog()
}

const deleteHardwareProfile = (item: HardwareProfile) => {
  hardwareProfileStore.setDeleteItem(item)
  hardwareProfileStore.openDeleteDialog()
}

const saveHardwareProfile = async (item: HardwareProfileFormPayload) => {
  const response = await hardwareProfileStore.saveHardwareProfile({
    ...item,
    project: scopedProjectId.value
  })
  if (!response) return

  hardwareProfileStore.closeCreateUpdateDialog()
  await fetchProfiles()
}

watch(
  () => [props.scope, props.projectId],
  () => {
    void hardwareProfileStore.fetchHardwareOptions()
    void fetchProfiles()
  },
  { immediate: true }
)
</script>

<template>
  <template v-if="fetchLoading">
    <div class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
  </template>
  <template v-else>
    <div class="hardware-profile-heading mb-6">
      <h1 class="hardware-profile-title">{{ title }}</h1>
    </div>

    <section class="hardware-profile-section">
      <DataTableWrapper
        title="VM Hardware Profiles"
        add-title="Add VM Hardware Profile"
        :headers="vmHeaders"
        :items="vmItems"
        :selected-items="selectedItems"
        :get-filtered-items="getFilteredItems"
        :can-change-title="false"
        :can-add-item="canManage"
        :can-change-item="false"
        :can-delete-item="false"
        :show-view-favorites-button="false"
        :show-move-to-archive-button="false"
        :show-duplicate-button="false"
        :show-delete-button="false"
        :hide-select-all="true"
        @add-item="addHardwareProfile"
        @update-selected-items="hardwareProfileStore.setSelectedItems"
      >
        <template #item="{ item }">
          <HardwareProfileTableRow
            :item="item as HardwareProfile"
            :can-manage="canManage"
            @edit="editHardwareProfile"
            @delete="deleteHardwareProfile"
          />
        </template>
      </DataTableWrapper>
    </section>

    <HardwareProfileDialog
      v-if="currentItem"
      v-model="createUpdateDialog"
      :item="currentItem"
      :project-id="scopedProjectId"
      :loading="writeLoading"
      @submit="saveHardwareProfile"
    />

    <DeleteDialog
      v-if="deleteItem"
      v-model="deleteDialog"
      :loading="deleteLoading"
      :name="deleteItem?.name"
      :item="deleteItem"
      @set-delete-item="deleteItem = $event"
      @delete="hardwareProfileStore.delete(deleteItem)"
    />
  </template>
</template>

<style scoped>
.hardware-profile-heading {
  align-items: center;
  display: flex;
  gap: 8px;
}

.hardware-profile-title {
  font-size: 28px;
  font-weight: 500;
  line-height: 1.2;
  margin: 0;
}

.hardware-profile-section + .hardware-profile-section {
  margin-top: 28px;
}
</style>
